import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { DxButtonModule, DxDataGridComponent, DxDataGridModule, DxLoadPanelModule, DxNumberBoxModule } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';
import { confirm } from 'devextreme/ui/dialog';
import { environment } from 'src/environments/environment';
import { CrudService } from '../../services/crud.service';
import { CanComponentDeactivate } from '../../services/can-deactivate.model';
import { DiscountValueCellComponent } from '../shared/discount-value-cell/discount-value-cell.component';

/**
 * Promotion-record discount view (Story 1.6, Design Spec §5.7 — UC-PROMO-013).
 *
 * A dedicated, themed page reached from the promotion-headers grid's "Review Discounts"
 * control-column button (`/pricing/promotion/:promotionHeaderId`). Negotiations are keyed per
 * promotion PERIOD (one discount per product/supplier per period), and a period can span many
 * promotion records — so the launching header resolves to its period and the page lists every
 * product in that promotion period. One line per product (a product's tiered pricing is now
 * resolved upstream via product_pricing / buying_group account_code, so this screen no longer
 * splits a product into Pour / Back Bar tier rows). Each product shows:
 *  - an "Agreed" value — read-only, reusing the Story 1.5 `app-discount-value-cell` primitive
 *    (the previously-agreed discount from the DTO `previous_discount_percent`); and
 *  - a "New" value — the customer's proposed discount (DTO `current_discount_percent`),
 *    editable as a `%`-only numeric input ONLY while the negotiation status is `draft`,
 *    read-only otherwise.
 *
 * Everything here is `%` only — never a `$`, list/unit/case price (BR-07). On launch the page
 * seeds/refreshes the promotion's negotiation rows by invoking the notify-free `reviews/open`
 * (Story 1.1) so rows exist at `draft` with no supplier email, then loads them through the single
 * Story 1.3 DTO. Saving "New" is a pure value-save that keeps `draft` (Decision 6) — the submit-to-
 * suppliers transition + email is Story 1.7, out of scope here.
 */
@Component({
  selector: 'app-promotion-discount-view',
  templateUrl: './promotion-discount-view.component.html',
  styleUrls: ['../shared/pricing-layout.css', './promotion-discount-view.component.css'],
  imports: [CommonModule, DxButtonModule, DxDataGridModule, DxNumberBoxModule, DxLoadPanelModule, DiscountValueCellComponent],
  standalone: true,
})
export class PromotionDiscountViewComponent implements OnInit, CanComponentDeactivate {
  /** The discount grid — used to reset its persisted layout from the toolbar. */
  @ViewChild(DxDataGridComponent) grid?: DxDataGridComponent;
  /** The promotion header the rows are scoped to (= the grid row id). */
  promotionHeaderId: number | null = null;
  /** Mapped discount rows — one per (product, supplier). */
  rows: DiscountRow[] = [];
  loading = true;
  loadError = false;
  /** A submit-to-suppliers request is in flight (guards the button + double-submit). */
  submitting = false;
  /** A batch "Save changes" request is in flight (guards the button + double-save). */
  savingAll = false;

  private apiUrl: string = environment.baseApiUrl;

  constructor(
    private crudService: CrudService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  async ngOnInit(): Promise<void> {
    const idParam = this.route.snapshot.paramMap.get('promotionHeaderId');
    const id = idParam !== null ? Number(idParam) : NaN;
    if (!Number.isFinite(id) || id < 1) {
      this.loading = false;
      this.loadError = true;
      notify('Invalid promotion reference', 'error', 6000);
      return;
    }
    this.promotionHeaderId = id;
    await this.loadDiscounts();
  }

  /**
   * Seed/refresh the rows (notify-free `reviews/open`, Story 1.1 — no supplier email) and then
   * load them through the Story 1.3 DTO. Seeding is reused, not duplicated (AC2).
   */
  private async loadDiscounts(): Promise<void> {
    this.loading = true;
    this.loadError = false;
    try {
      // Seed/refresh at `draft` with no email. A seed failure must not silently strand the page —
      // surface it but still attempt the load so any already-seeded rows render.
      try {
        await firstValueFrom(
          this.crudService.insertData(`${this.apiUrl}reviews/open`, { promotion_header_id: this.promotionHeaderId }),
        );
      } catch {
        notify('Could not refresh discounts from current pricing — showing the latest saved values', 'warning', 6000);
      }

      const response: any = await firstValueFrom(
        this.crudService.newGetData(`${this.apiUrl}reviews/promotion-discounts/${this.promotionHeaderId}`),
      );
      const data: any[] = response?.data ?? [];
      this.rows = data.map((dto) => this.toRow(dto));
    } catch (error) {
      console.error('Failed to load promotion discounts', error);
      this.loadError = true;
      notify('Failed to load discounts', 'error', 6000);
    } finally {
      this.loading = false;
    }
  }

  /**
   * Re-read the rows through the GET DTO only (no re-seed). Used after a submit (so the transitioned
   * rows render read-only) and by Revert changes (to discard unsaved edits back to the last-saved
   * state) — deliberately NOT loadDiscounts(), whose notify-free `reviews/open` change-detection could
   * flip a just-submitted row back to `draft` (AC8).
   */
  private async reloadRows(): Promise<void> {
    if (this.promotionHeaderId === null) {
      return;
    }
    try {
      const response: any = await firstValueFrom(
        this.crudService.newGetData(`${this.apiUrl}reviews/promotion-discounts/${this.promotionHeaderId}`),
      );
      const data: any[] = response?.data ?? [];
      this.rows = data.map((dto) => this.toRow(dto));
    } catch (error) {
      console.error('Failed to reload discounts', error);
      notify('Could not refresh the view — reload the page to see the latest', 'warning', 6000);
    }
  }

  /** Map a Story 1.3 DTO row to the editable view model — one line per product. */
  private toRow(dto: any): DiscountRow {
    const newDiscount = this.toNumberOrNull(dto.current_discount_percent);
    const discountDollar = this.toNumberOrNull(dto.discount_dollar);

    return {
      id: dto.id,
      productCode: dto.product_code,
      productName: dto.product_name ?? dto.product_code,
      supplierCode: dto.supplier_code ?? '',
      supplierName: dto.supplier_name ?? '',
      agreed: this.toNumberOrNull(dto.previous_discount_percent),
      // The raw `$` allowance (discount_dollar = allowance_group) for the editable Discount column.
      // This endpoint deliberately surfaces it (BR-07 override, this screen only); other screens stay %-only.
      discountDollar,
      newDiscount,
      // $/% conversion factor (= cost ÷ 100, since $ = % × cost ÷ 100). Prefer the exposed
      // conversion cost so EVERY row converts — including zero/blank-discount rows where the
      // $ ÷ % ratio is indeterminate; fall back to that ratio only if the cost is absent. Null when
      // neither is available (e.g. a price-data-missing row with no cost) — then editing one field
      // leaves the other as-is.
      factor: this.factorFor(this.toNumberOrNull(dto.conversion_cost), discountDollar, newDiscount),
      status: dto.status ?? '',
      isDraft: dto.status === 'draft',
      priceDataMissing: !!dto.price_data_missing,
      error: null,
      dirty: false,
      saving: false,
    };
  }

  /**
   * The `$`-per-percentage-point conversion factor for a product. Prefers the exposed conversion cost
   * (`cost ÷ 100`), which works for every row; falls back to the seeded `$ ÷ %` ratio when the cost is
   * absent. Null when neither is available (no cost, and no/zero seed %) — the row can't convert.
   */
  private factorFor(cost: number | null, dollar: number | null, percent: number | null): number | null {
    if (cost !== null && cost > 0) {
      return cost / 100;
    }
    if (dollar === null || percent === null || percent === 0) {
      return null;
    }
    return dollar / percent;
  }

  /** Round to 2 decimal places (the display + validation precision for both `%` and `$`). */
  private round2(value: number): number {
    return Math.round(value * 100) / 100;
  }

  private toNumberOrNull(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }

  /**
   * A New (%) value changed — record the edit, re-validate it, and recalculate the
   * Discount ($) from the conversion factor (`$ = % × factor`) when one is available. Marks dirty.
   */
  onNewValueChanged(row: DiscountRow, event: any): void {
    const value: number | null = event?.value ?? null;
    if (value === row.newDiscount) {
      // No-op: ignore the initial programmatic [value] bind (and any re-emit of the same value)
      // so the row isn't falsely marked dirty and CanDeactivateGuard doesn't prompt without an edit.
      return;
    }
    row.newDiscount = value;
    row.error = this.validate(value);
    if (row.factor !== null && value !== null) {
      row.discountDollar = this.round2(value * row.factor);
    }
    row.dirty = true;
  }

  /**
   * A Discount ($) value changed — record the edit and recalculate the New (%) from the
   * conversion factor (`% = $ ÷ factor`) when one is available, re-validating the recomputed %.
   * The % stays the authoritative saved value; the $ is the display partner. Marks dirty.
   */
  onDiscountValueChanged(row: DiscountRow, event: any): void {
    const value: number | null = event?.value ?? null;
    if (value === row.discountDollar) {
      return; // ignore the initial programmatic bind / same-value re-emit (see onNewValueChanged)
    }
    row.discountDollar = value;
    if (row.factor !== null && row.factor !== 0 && value !== null) {
      row.newDiscount = this.round2(value / row.factor);
      row.error = this.validate(row.newDiscount);
    }
    row.dirty = true;
  }

  /**
   * Validate a New discount value: numeric, 0 ≤ x < 100, ≤ 2 decimal places (AC5).
   * Returns an inline message, or null when valid.
   */
  validate(value: number | null): string | null {
    if (value === null || value === undefined) {
      return 'Enter a discount percentage';
    }
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      return 'Must be a number';
    }
    if (value < 0 || value >= 100) {
      return 'Must be 0 or more and less than 100';
    }
    if (Number(value.toFixed(2)) !== value) {
      return 'Maximum 2 decimal places';
    }
    return null;
  }

  /**
   * Format a `$` allowance for the Discount column: `$12.50`, or an em-dash for a missing value
   * (never a faked `$0.00`). Mirrors the discount-value-cell's null handling.
   */
  formatDollar(value: number | null): string {
    if (value === null || value === undefined || !Number.isFinite(value)) {
      return '—';
    }
    return `$${value.toFixed(2)}`;
  }

  /** True when the row has a validation error blocking save. */
  hasError(row: DiscountRow): boolean {
    return row.error !== null;
  }

  /** Draft rows carrying an unsaved New edit — exactly what a batch save would write. */
  get dirtyRows(): DiscountRow[] {
    return this.rows.filter((r) => r.isDraft && r.dirty);
  }

  /** True when there is at least one unsaved New edit to save. */
  get hasUnsavedChanges(): boolean {
    return this.dirtyRows.length > 0;
  }

  /** The "Save changes" control is enabled only while ≥ 1 dirty row exists and no save is in flight. */
  get canSaveAll(): boolean {
    return this.hasUnsavedChanges && !this.savingAll;
  }

  /**
   * Batch-save every dirty draft row's New discount(s) in one request (Story 1.6 — batch save).
   *
   * The grid-level companion to the old per-row Save: it re-validates every configured tier on every
   * dirty row, PUTs the whole set to `reviews/discounts` (a pure value-save — status stays `draft`,
   * no supplier notification, Decision 6), then clears `dirty` on the rows the server saved and flags
   * inline any the server rejected (best-effort — e.g. a row another session already submitted).
   */
  async saveAll(): Promise<void> {
    if (!this.canSaveAll) {
      return;
    }
    const dirty = this.dirtyRows;

    // Re-validate every dirty row before sending (AC5).
    let blocked = false;
    for (const row of dirty) {
      row.error = this.validate(row.newDiscount);
      if (this.hasError(row)) {
        blocked = true;
      }
    }
    if (blocked) {
      notify('Fix the highlighted discount values before saving', 'error', 4000);
      return;
    }

    // The % is the authoritative saved value; the $ allowance is sent alongside so the stored display
    // snapshot (discount_dollar) stays consistent with the % on reload.
    const discounts = dirty.map((row) => ({
      id: row.id,
      discount_percent: row.newDiscount as number,
      discount_dollar: row.discountDollar,
    }));

    this.savingAll = true;
    try {
      const response: any = await firstValueFrom(
        this.crudService.updateDataWithoutId(`${this.apiUrl}reviews/discounts`, { discounts }),
      );
      const data: any = response?.data ?? {};
      const savedIds: number[] = data.saved ?? [];
      const failed: Array<{ id: number; reason?: string }> = data.failed ?? [];

      // Clear dirty on the rows the server saved.
      const savedSet = new Set<number>(savedIds);
      for (const row of dirty) {
        if (savedSet.has(row.id)) {
          row.dirty = false;
        }
      }

      if (savedIds.length > 0) {
        notify(`Saved ${savedIds.length} ${savedIds.length === 1 ? 'product' : 'products'}`, 'success', 2000);
      }
      if (failed.length > 0) {
        // Flag each rejected row inline (keeps it dirty) so the customer sees which ones did not save.
        for (const f of failed) {
          const row = this.rows.find((r) => r.id === f.id);
          if (row) {
            row.error = f.reason ?? 'Could not be saved';
          }
        }
        notify(
          `${failed.length} ${failed.length === 1 ? 'product' : 'products'} could not be saved`,
          'warning',
          6000,
        );
      }
    } catch (error) {
      console.error('Failed to batch-save discounts', error);
      notify('Could not save the discounts — please try again', 'error', 6000);
    } finally {
      this.savingAll = false;
    }
  }

  /**
   * The draft rows a submit would transition (the customer's New values). price_data_missing rows
   * are excluded — they carry no valid % to send, so they are not submittable (Story 1.7 review).
   */
  get draftRows(): DiscountRow[] {
    return this.rows.filter((r) => r.isDraft && !r.priceDataMissing);
  }

  /** Number of products a submit would send to suppliers. */
  get submittableCount(): number {
    return this.draftRows.length;
  }

  /** Distinct connected suppliers among the draft rows — one grouped email each (BR-NTF-02). */
  get submitSupplierCount(): number {
    return new Set(this.draftRows.map((r) => r.supplierCode)).size;
  }

  /** The "Submit to suppliers" control is enabled only while ≥ 1 product is in draft (AC1). */
  get canSubmit(): boolean {
    return this.submittableCount > 0;
  }

  /**
   * Submit every draft product on this promotion to its supplier(s) (Story 1.7, AC1/AC8).
   *
   * Confirms the product + supplier counts (no `$`, no per-row detail), POSTs the submit
   * transition, surfaces the grouped-email outcome (success toast; warning toast when any send
   * failed — mirroring Story 1.2's `notifications.failed` pattern), then re-reads the rows so the
   * submitted rows render read-only and the control disables (no draft rows remain).
   */
  async submitToSuppliers(): Promise<void> {
    if (!this.canSubmit || this.submitting || this.promotionHeaderId === null) {
      return;
    }

    // Block submit while a New edit is unsaved (Story 1.7 review): the API transitions the last
    // *saved* %, so submitting now would email the supplier a value that differs from the screen,
    // and the reload would silently discard the edit. Make the customer save first.
    if (this.rows.some((r) => r.dirty)) {
      notify('Save your discount changes before submitting to suppliers', 'warning', 5000);
      return;
    }

    const productCount = this.submittableCount;
    const supplierCount = this.submitSupplierCount;
    const productWord = productCount === 1 ? 'product' : 'products';
    const supplierWord = supplierCount === 1 ? 'supplier' : 'suppliers';

    const confirmed = await confirm(
      `Submit ${productCount} ${productWord} to ${supplierCount} ${supplierWord}? ` +
        `The ${supplierWord} will be emailed and asked to review the discounts in the portal.`,
      'Submit to suppliers',
    );
    if (!confirmed) {
      return;
    }

    await this.performSubmit();
  }

  /**
   * POST the submit transition, surface the grouped-email outcome (success toast; warning toast
   * when any send failed — mirroring Story 1.2's `notifications.failed` pattern), then re-read
   * the rows (GET only) so submitted rows render read-only and the control disables (AC8).
   *
   * Split from {@link submitToSuppliers} so the dialog-free core is unit-testable (the DevExtreme
   * `confirm()` promise only resolves on a real user click).
   */
  private async performSubmit(): Promise<void> {
    if (this.promotionHeaderId === null) {
      return;
    }
    this.submitting = true;
    try {
      const response: any = await firstValueFrom(
        this.crudService.insertData(`${this.apiUrl}reviews/submit`, { promotion_header_id: this.promotionHeaderId }),
      );
      const data: any = response?.data ?? {};
      const submitted: number = data.submitted ?? 0;
      const supplierTotal: number = data.supplier_count ?? 0;
      const failed: any[] = data.notifications?.failed ?? [];

      if (submitted === 0) {
        // No-op result (e.g. another session submitted first between load and click) — don't
        // report a green "Submitted 0 products" success (Story 1.7 review).
        notify('Nothing to submit — these discounts are already with the suppliers', 'info', 5000);
      } else {
        notify(
          `Submitted ${submitted} ${submitted === 1 ? 'product' : 'products'} to ` +
            `${supplierTotal} ${supplierTotal === 1 ? 'supplier' : 'suppliers'}`,
          'success',
          3000,
        );
      }
      if (failed.length > 0) {
        notify(
          `${failed.length} ${failed.length === 1 ? 'supplier' : 'suppliers'} could not be notified — ` +
            `you can submit again to retry`,
          'warning',
          8000,
        );
      }

      // Re-read (GET only) so submitted rows render read-only and the control disables (AC8).
      await this.reloadRows();
    } catch (error) {
      console.error('Failed to submit review', error);
      notify('Could not submit the review — please try again', 'error', 6000);
    } finally {
      this.submitting = false;
    }
  }

  /**
   * Inject "Revert changes" and "Reset layout" buttons into the grid toolbar (before the built-in
   * search/column-chooser items). Revert discards unsaved discount edits; Reset layout discards the
   * persisted column order/width/sort/filter/visibility.
   */
  onToolbarPreparing(e: any): void {
    e.toolbarOptions.items.unshift(
      {
        location: 'after',
        widget: 'dxButton',
        options: {
          icon: 'undo',
          text: 'Revert changes',
          hint: 'Discard unsaved discount changes and restore the last-saved values',
          onClick: () => this.revertChanges(),
        },
      },
      {
        location: 'after',
        widget: 'dxButton',
        options: {
          icon: 'revert',
          text: 'Reset layout',
          hint: 'Reset the columns, sorting and filters to the default layout',
          onClick: () => this.resetLayout(),
        },
      },
    );
  }

  /**
   * Reset the grid to its default layout. `state(null)` clears the in-memory state and, because
   * state-storing is enabled, overwrites the saved localStorage layout with the default.
   */
  resetLayout(): void {
    this.grid?.instance.state(null);
  }

  /**
   * Revert unsaved discount edits — discard every dirty New (%) / Discount ($) change and restore the
   * last-saved values by re-reading the rows (GET only). No-op when nothing is unsaved; confirms first
   * because the edits are discarded.
   */
  async revertChanges(): Promise<void> {
    if (this.savingAll || !this.hasUnsavedChanges) {
      return;
    }
    const confirmed = await confirm('Discard your unsaved discount changes?', 'Revert changes');
    if (!confirmed) {
      return;
    }
    await this.performRevert();
  }

  /**
   * Re-read the rows (GET only) so every unsaved edit is discarded back to the last-saved state.
   * Split from {@link revertChanges} so the dialog-free core is unit-testable (the DevExtreme
   * `confirm()` promise only resolves on a real user click).
   */
  private async performRevert(): Promise<void> {
    await this.reloadRows();
  }

  /** Navigate back to the promotions grid. */
  back(): void {
    this.router.navigate(['/promotions']);
  }

  /** CanDeactivateGuard — prompt when navigating away with unsaved New edits. */
  canDeactivate(): boolean | Promise<boolean> {
    const hasUnsaved = this.rows.some((r) => r.dirty);
    if (!hasUnsaved) {
      return true;
    }
    return confirm('You have unsaved discount changes. Leave without saving?', 'Unsaved changes');
  }
}

/** Editable view model for one product's discount row (one line per product — no tiers). */
export interface DiscountRow {
  id: number;
  productCode: string;
  productName: string;
  supplierCode: string;
  supplierName: string;
  /** Agreed (previously-agreed) discount — read-only. */
  agreed: number | null;
  /** Raw `$` allowance (discount_dollar = allowance_group) for the Discount column. */
  discountDollar: number | null;
  /** `$ ÷ %` conversion factor (= cost ÷ 100), or null when not derivable. Drives the
   *  two-way recalculation between the New (%) and Discount ($) fields without exposing the cost. */
  factor: number | null;
  /** New (proposed) discount — editable working copy while `draft`. */
  newDiscount: number | null;
  status: string;
  isDraft: boolean;
  priceDataMissing: boolean;
  error: string | null;
  dirty: boolean;
  saving: boolean;
}
